// ImageSlider.jsx
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import classes from "./ImageSlider.module.css";
import { Virtuoso } from "react-virtuoso";
import { Icon, Notifications, Spinner } from "@jds/core";
import {
  addParamsURL,
  downloadFile,
  getFileNameFromDisposition,
} from "../../util/helper";
import decryptUrl from "../../util/img-decryption";
import Loader from "../UI/Loader/Loader";
import brokenImg from "../../assets/png/brokenImg.png";
import useApiHttp from "../../hooks/use-http";
import { useParams } from "react-router-dom";
import {
  openFileSideBar,
  setFileDetails,
} from "../../store/state-slices/fileDetailsSidebar";
import { useDispatch } from "react-redux";

const ImageSlider = ({
  toggler = false,
  slide = 0,
  totalCount = 0,
  sources = [],
  onClose,
  endReached = () => {},
  loading = false,
  deleteImage = () => {},
  isAdmin = false,
}) => {
  const eventFID = localStorage.getItem("eventFID");
  const { fid: folderId, id: eventId } = useParams();
  const dispatch = useDispatch();

  const [currentSlide, setCurrentSlide] = useState(slide);
  const virtuosoRef = useRef(null);
  const [mouseVisible, setMouseVisible] = useState(true);
  const [fullScreen, setFullScreen] = useState(false);
  const [fullWindow, setFullWindow] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);
  const [slidePlaying, setSlidePlaying] = useState(false);
  const [slideInterval, setSlideInterval] = useState(3000);
  const hideTimerRef = useRef(null);
  const sideShowIntervalRef = useRef(null);
  const imageWrapperRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const viewerRef = useRef(null);
  const imageRef = useRef(null);

  const dragRef = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
  });

  const pinchRef = useRef({
    initialDistance: 0,
    initialZoom: 1,
  });

  const { sendRequest: thumbnailRequest, isLoading: isThumbnailRequest } =
    useApiHttp();
  const { sendRequest: downloadRequest } = useApiHttp();

  const getImageURL = useCallback((url, height, width) => {
    return addParamsURL(decryptUrl(url), {
      size: height && width ? "v" : "l",
      height: height ? getScaledDimensions(width, height)?.height : 0,
      width: height ? getScaledDimensions(width, height)?.width : 0,
      devicetype: "web",
      imgType: "webp",
    });
  }, []);
  const preloadImage = useCallback((src) => {
    const img = new Image();
    img.src = src;
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === sources.length - 1 ? 0 : prev + 1));
  }, [sources.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? sources.length - 1 : prev - 1));
  }, [sources.length]);

  const zoomIn = () => setZoom((z) => Math.min(z + 0.25, 2));

  const zoomOut = () => {
    setZoom((z) => {
      const next = Math.max(z - 0.25, 1);
      if (next === 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  };

  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  // --------------------
  // Helpers
  // --------------------
  const getDistance = (touches) => {
    const [a, b] = touches;
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
  };

  const getBounds = () => {
    const viewer = viewerRef.current;
    const img = imageRef.current;

    if (!viewer || !img) return { maxX: 0, maxY: 0 };

    const viewerRect = viewer.getBoundingClientRect();

    const renderedWidth = img.offsetWidth;
    const renderedHeight = img.offsetHeight;

    const scaledWidth = renderedWidth * zoom;
    const scaledHeight = renderedHeight * zoom;

    const maxX = Math.max(0, (scaledWidth - viewerRect.width) / 2);
    const maxY = Math.max(0, (scaledHeight - viewerRect.height) / 2);

    return { maxX, maxY };
  };

  const clampPosition = (x, y) => {
    const { maxX, maxY } = getBounds();

    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  };

  // --------------------
  // Mouse drag
  // --------------------
  const handleMouseDown = (e) => {
    if (zoom <= 1) return;

    dragRef.current.dragging = true;
    dragRef.current.startX = e.clientX - position.x;
    dragRef.current.startY = e.clientY - position.y;
  };

  const handleMouseMove = (e) => {
    if (!dragRef.current.dragging) return;

    const rawX = e.clientX - dragRef.current.startX;
    const rawY = e.clientY - dragRef.current.startY;

    setPosition(clampPosition(rawX, rawY));
  };

  const handleMouseUp = () => {
    dragRef.current.dragging = false;
  };

  // --------------------
  // Touch (pan + pinch)
  // --------------------
  const handleTouchStart = (e) => {
    if (e.touches.length === 1 && zoom > 1) {
      const t = e.touches[0];

      dragRef.current.dragging = true;
      dragRef.current.startX = t.clientX - position.x;
      dragRef.current.startY = t.clientY - position.y;
    }

    if (e.touches.length === 2) {
      dragRef.current.dragging = false;

      pinchRef.current.initialDistance = getDistance(e.touches);

      pinchRef.current.initialZoom = zoom;
    }
  };

  const handleTouchMove = (e) => {
    // pinch zoom
    if (e.touches.length === 2) {
      const distance = getDistance(e.touches);

      const scale = distance / pinchRef.current.initialDistance;

      const nextZoom = Math.min(
        5,
        Math.max(1, pinchRef.current.initialZoom * scale),
      );

      setZoom(nextZoom);

      if (nextZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }

      return;
    }

    // pan
    if (e.touches.length === 1 && dragRef.current.dragging && zoom > 1) {
      const t = e.touches[0];

      const rawX = t.clientX - dragRef.current.startX;
      const rawY = t.clientY - dragRef.current.startY;

      setPosition(clampPosition(rawX, rawY));
    }
  };

  const handleTouchEnd = () => {
    dragRef.current.dragging = false;
  };

  const downloadHande = (mediaId) => {
    downloadRequest(
      {
        url: `organizer/download`,
        method: "POST",
        base: "MEDIA",
        // formData: true,
        body: {
          mediaId,
          eventId,
        },
      },
      null,
      (data, res) => {
        const imageUrl = URL.createObjectURL(data);
        const fileName = getFileNameFromDisposition(res);
        downloadFile(imageUrl, fileName);
      },
      (err, data) => {
        Notifications.toast.error({
          id: "download-photos-error",
          title: data?.message,
        });
      },
    );
  };

  const thumbnailUpdate = (thumbnail) => {
    thumbnailRequest(
      {
        url: `event/update-thumbnail`,
        method: "PUT",
        body: {
          eventId,
          folderId: eventFID,
          thumbnail,
        },
        // formData: true,
      },
      null,
      (data) => {
        Notifications.toast.success({
          id: "upload-thumbnail",
          title: data?.message,
        });
      },
      (err, data) => {
        Notifications.toast.error({
          id: "upload-thumbnail",
          title: data?.message,
        });
      },
    );
  };

  const resetHideTimer = useCallback(() => {
    setMouseVisible(true);

    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }

    hideTimerRef.current = setTimeout(() => {
      setMouseVisible(false);
    }, 2500);
  }, []);

  useEffect(() => {
    setCurrentSlide(slide);
  }, [slide, toggler]);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!toggler) return;

      if (e.key === "ArrowRight" && sources.length - 1 > currentSlide)
        nextSlide();
      if (e.key === "ArrowLeft" && currentSlide !== 0) prevSlide();
      if (e.key === "Escape") onClose?.();
      if (e.key === "f") setFullWindow((pre) => !pre);
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [toggler, nextSlide, prevSlide, onClose, currentSlide, sources]);

  useEffect(() => {
    if (!toggler) return;

    const el = imageWrapperRef.current;
    if (!el) return;

    const handleActivity = () => resetHideTimer();

    el.addEventListener("mousemove", handleActivity);
    el.addEventListener("mousedown", handleActivity);
    el.addEventListener("touchstart", handleActivity);
    el.addEventListener("click", handleActivity);

    resetHideTimer();

    return () => {
      el.removeEventListener("mousemove", handleActivity);
      el.removeEventListener("mousedown", handleActivity);
      el.removeEventListener("touchstart", handleActivity);
      el.removeEventListener("click", handleActivity);

      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [toggler, resetHideTimer]);

  useEffect(() => {
    if (toggler) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [toggler]);

  useEffect(() => {
    virtuosoRef.current?.scrollToIndex({
      index: Number(currentSlide),
      behavior: "instant",
      align: "center",
    });
    resetZoom();
  }, [currentSlide, virtuosoRef]);

  useEffect(() => {
    if (slidePlaying) {
      sideShowIntervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => {
          if (prev === sources.length - 1) {
            return 0; // loop back
          }
          return prev + 1;
        });
      }, slideInterval); // 4 sec
    }

    return () => clearInterval(sideShowIntervalRef.current);
  }, [slidePlaying, sources.length, slideInterval]);
  useEffect(() => {
    const handleFullscreen = async () => {
      try {
        if (fullWindow) {
          if (!document.fullscreenElement) {
            await document.documentElement.requestFullscreen();
          }
        } else {
          if (document.fullscreenElement) {
            await document.exitFullscreen();
          }
        }
      } catch (err) {
        console.error("Fullscreen error:", err);
      }
    };

    handleFullscreen();
  }, [fullWindow]);
  //Preload next and previous img
  useEffect(() => {
    setImgLoading(true);

    const img = new Image();
    img.src = getImageURL(
      sources[currentSlide]?.thumbnailFileSignedPath,
      sources[currentSlide]?.metaInfo?.height,
      sources[currentSlide]?.metaInfo?.width,
    );

    img.onload = () => setImgLoading(false);

    if (currentSlide + 1 < sources.length) {
      preloadImage(
        getImageURL(
          sources[currentSlide + 1]?.thumbnailFileSignedPath,
          sources[currentSlide + 1]?.metaInfo?.height,
          sources[currentSlide + 1]?.metaInfo?.width,
        ),
      );
    }
    if (currentSlide - 1 >= 0) {
      preloadImage(
        getImageURL(
          sources[currentSlide - 1]?.thumbnailFileSignedPath,
          sources[currentSlide - 1]?.metaInfo?.height,
          sources[currentSlide - 1]?.metaInfo?.width,
        ),
      );
    }
  }, [sources, currentSlide]);
  if (!toggler || sources.length === 0) return null;

  return (
    <div className={classes["slider-overlay"]}>
      <div className={classes["slider-container"]}>
        {/* Image Area */}
        <div className={classes["image-wrapper"]} ref={imageWrapperRef}>
          <div
            className={`${classes["image-header"]} ${mouseVisible && classes["show"]}`}>
            <div className={classes["header-left"]}>
              <span
                style={{
                  "--color-primary-grey-60": "white",
                  cursor: "pointer",
                }}
                onClick={onClose}>
                <Icon ic={"IcArrowBack"} color="grey-60" />
              </span>
              <div className={classes["file-detail"]}>
                <span
                  className={classes["file-name"]}
                  title={sources[currentSlide]?.fileName}>
                  {sources[currentSlide]?.fileName}
                </span>

                <span
                  className={classes["up-time"]}
                  title={`${sources[currentSlide]?.userName} (${sources[currentSlide]?.uploaderType}) at ${formatCreatedAt(sources[currentSlide]?.createdAt)} ${utcToIST(sources[currentSlide]?.createdAtUTC)}`}>
                  ~ {sources[currentSlide]?.userName} (
                  {sources[currentSlide]?.uploaderType}) at&nbsp;
                  {formatCreatedAt(sources[currentSlide]?.createdAt)}&nbsp;
                  {utcToIST(sources[currentSlide]?.createdAtUTC)}
                </span>
              </div>
            </div>

            <div className={classes["options-section"]}>
              {!slidePlaying && (
                <>
                  {zoom > 1 && (
                    <span
                      style={{
                        "--color-primary-grey-60": "white",
                      }}
                      title="Reset zoom"
                      onClick={() => {
                        resetZoom();
                      }}>
                      <Icon ic={"IcPhotoSize"} color="grey-60" />
                    </span>
                  )}

                  <span
                    style={{
                      "--color-primary-grey-60": "white",
                    }}
                    title="Zoom out"
                    onClick={() => {
                      zoomOut();
                    }}>
                    <Icon ic={"IcZoomOut"} color="grey-60" />
                  </span>
                  <span
                    style={{
                      "--color-primary-grey-60": "white",
                    }}
                    title="Zoom in"
                    onClick={() => {
                      zoomIn();
                    }}>
                    <Icon ic={"IcZoomIn"} color="grey-60" />
                  </span>
                  <span
                    style={{
                      "--color-primary-grey-60": "white",
                    }}
                    title={fullScreen ? "Show thumbnail" : "Hide thumbnail"}
                    onClick={() => {
                      setFullScreen((pre) => !pre);
                    }}>
                    <Icon ic={"IcThumbnail"} color="grey-60" />
                  </span>
                  <span
                    style={{
                      "--color-primary-grey-60": "white",
                    }}
                    title={
                      fullWindow ? "Exit full screen" : "Enter full screen"
                    }
                    onClick={() => {
                      setFullWindow((pre) => !pre);
                    }}>
                    <Icon
                      ic={fullWindow ? "IcCompress" : "IcExtract"}
                      color="grey-60"
                    />
                  </span>
                  {isAdmin && (
                    <>
                      <span
                        style={{
                          "--color-primary-grey-60": "white",
                        }}
                        title="Delete"
                        onClick={() => {
                          deleteImage(sources[currentSlide]?.id);
                        }}>
                        <Icon ic={"IcTrash"} color="grey-60" />
                      </span>
                      <span
                        style={{
                          "--color-primary-grey-60": "white",
                        }}
                        title="Download"
                        onClick={() => {
                          downloadHande(sources[currentSlide]?.id);
                        }}>
                        <Icon ic={"IcDownload"} color="grey-60" />
                      </span>
                      <span
                        style={{
                          "--color-primary-grey-60": "white",
                        }}
                        title="Set thumbnail"
                        onClick={() => {
                          thumbnailUpdate(
                            decryptUrl(
                              sources[currentSlide]?.reducedFileSignedPath,
                            ),
                          );
                        }}>
                        <Icon ic={"IcScanPicture"} color="grey-60" />
                      </span>
                    </>
                  )}
                  <span
                    style={{
                      "--color-primary-grey-60": "white",
                    }}
                    title="Image info"
                    onClick={(e) => {
                      dispatch(setFileDetails(sources[currentSlide]));
                      dispatch(openFileSideBar());
                    }}>
                    <Icon ic={"IcInfo"} color="grey-60" />
                  </span>
                </>
              )}
              {slidePlaying && (
                <>
                  <span
                    style={{
                      "--color-primary-grey-60": "white",
                    }}
                    title={"Slow down"}
                    onClick={() => {
                      setSlideInterval((pre) => Math.max(pre - 1000, 3000));
                    }}>
                    <Icon ic={"IcMinimise"} color="grey-60" />
                  </span>
                  <span
                    className={classes["speed-value"]}
                    style={{
                      cursor: "default",
                    }}>
                    {slideInterval / 1000}s
                  </span>
                  <span
                    style={{
                      "--color-primary-grey-60": "white",
                    }}
                    title={"Speed up"}
                    onClick={() => {
                      setSlideInterval((pre) => Math.min(pre + 1000, 10000));
                    }}>
                    <Icon ic={"IcAddCircle"} color="grey-60" />
                  </span>
                </>
              )}
              <>
                <span
                  style={{
                    "--color-primary-grey-60": "white",
                  }}
                  title={slidePlaying ? "Pause" : "Slide show"}
                  onClick={() => {
                    setSlidePlaying((pre) => !pre);
                  }}>
                  <Icon
                    ic={slidePlaying ? "IcPauseCircle" : "IcPlayCircle"}
                    color="grey-60"
                  />
                </span>
              </>
            </div>
          </div>
          {/* Previous */}
          {sources.length > 1 && currentSlide !== 0 && (
            <span
              className={`${classes["nav-btn"]} ${classes["left-btn"]} ${classes["right-btn"]} ${mouseVisible && classes["show"]}`}
              onClick={prevSlide}
              style={{
                "--color-primary-grey-60": "white",
              }}>
              <Icon ic={"IcBack"} color="grey-60" />
            </span>
          )}
          {imgLoading ? (
            <Spinner labelPosition="right" size="large" kind="inline" />
          ) : (
            <div
              ref={viewerRef}
              style={{
                aspectRatio:
                  sources[currentSlide]?.metaInfo?.width /
                  sources[currentSlide]?.metaInfo?.height,
                maxHeight: "100%",
                maxWidth: "100%",
              }}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}>
              <img
                ref={imageRef}
                src={getImageURL(
                  sources[currentSlide]?.thumbnailFileSignedPath,
                  sources[currentSlide]?.metaInfo?.height,
                  sources[currentSlide]?.metaInfo?.width,
                )}
                alt={`slide-${currentSlide}`}
                className={classes["slider-image"]}
                draggable={false}
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                  transition: dragRef.current.dragging
                    ? "none"
                    : "transform 150ms ease",
                  cursor:
                    zoom > 1
                      ? dragRef.current.dragging
                        ? "grabbing"
                        : "grab"
                      : "default",
                  objectFit: "fill",
                  width: "100%",
                  height: "100%",
                }}
                onMouseDown={handleMouseDown}
                // onClick={() => {
                //   setFullScreen((pre) => !pre);
                // }}
              />
            </div>
          )}

          {/* Next */}
          {sources.length > 1 && sources.length - 1 > currentSlide && (
            <span
              className={`${classes["nav-btn"]} ${classes["right-btn"]} ${mouseVisible && classes["show"]}`}
              onClick={nextSlide}
              style={{
                "--color-primary-grey-60": "white",
              }}>
              <Icon ic={"IcNext"} color="grey-60" />
            </span>
          )}

          {/* <div className={classes["image-bottom"]}>
            <p>hello</p>
          </div> */}
        </div>

        {/* Thumbnails */}

        {/* <div
          className={`${classes["thumbnail-container"]} ${!fullScreen && classes["show"]}`}>
          {sources.map((source, index) => (
            <div
              className={`${classes["thumbnail"]} ${index == currentSlide && classes["active"]}`}>
              <img
                draggable={false}
                key={index}
                src={addParamsURL(decryptUrl(source?.thumbnailFileSignedPath), {
                  size: "s",
                  devicetype: "web",
                  imgType: "webp",
                })}
                alt={`${source?.fileName}`}
                onClick={() => setCurrentSlide(index)}
              />
            </div>
          ))}
        </div> */}
        <div>
          <Virtuoso
            ref={virtuosoRef}
            className={`${classes["thumbnail-container"]} ${!fullScreen && classes["show"]}`}
            data={sources}
            totalCount={totalCount}
            itemContent={(index, source) => (
              <ImageThumbnail
                index={index}
                source={source}
                sources={sources}
                loading={loading}
                endReached={endReached}
                currentSlide={currentSlide}
                setCurrentSlide={setCurrentSlide}
              />
            )}
            initialTopMostItemIndex={slide}
            horizontalDirection
            endReached={endReached}
          />
        </div>
      </div>
    </div>
  );
};
export default ImageSlider;

function formatCreatedAt(createdAt) {
  const date = new Date(
    createdAt[0],
    createdAt[1] - 1,
    createdAt[2],
    createdAt[3],
    createdAt[4],
    createdAt[5],
    Math.floor(createdAt[6] / 1_000_000),
  );

  const now = new Date();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const targetDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  const diffDays = Math.floor((today - targetDay) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return `Today`;
  }

  if (diffDays === 1) {
    return `Yesterday`;
  }

  // Day first format: DD/MM/YYYY
  const fullDate = date.toLocaleDateString("en-GB");

  return `${fullDate}`;
}
function utcToIST(timestamp) {
  const date = new Date(timestamp * 1000);

  return date
    .toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    })
    .toUpperCase();
}
function getScaledDimensions(
  originalWidth,
  originalHeight,
  targetHeight = 1962,
) {
  let width = originalWidth;
  let height = originalHeight;

  // Calculate scale ratio
  const scale = targetHeight / height;

  // Return scaled dimensions
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}
function ImageThumbnail({
  index,
  source,
  endReached = () => {},
  loading = false,
  sources = [],
  currentSlide = 0,
  setCurrentSlide = () => {},
}) {
  return (
    <div className={classes["thumbnail-flex"]}>
      <div
        className={`${classes["thumbnail"]} ${index == currentSlide && classes["active"]}`}>
        <img
          draggable={false}
          key={index}
          src={addParamsURL(decryptUrl(source?.thumbnailFileSignedPath), {
            size: "s",
            devicetype: "web",
            imgType: "webp",
          })}
          alt={`${source?.fileName}`}
          onClick={() => setCurrentSlide(index)}
          onError={(e) => {
            // setErrorImg(true);
            e.target.src = brokenImg;
          }}
        />
      </div>
      {index == sources.length - 1 && loading && (
        <div
          className={`${classes["thumbnail"]} ${classes["thumbnail-loader"]}`}>
          <Spinner labelPosition="right" size="small" />
        </div>
      )}
    </div>
  );
}
